// Hexagon shape strategies (horizontal and vertical orientations)

import { registerShape } from '../services/ShapeRegistry';
import { findEdgeSideIntersection } from '../utils/geometryUtils';
import type { LineSeg } from '../utils/geometryUtils';
import type {
  ShapeDefinition,
  ShapeDrawSettings,
  EdgeData,
  Point,
  CollisionShape,
} from '../types/ShapeTypes';

type Orientation = 'horizontal' | 'vertical';

function getVertices(
  cx: number,
  cy: number,
  w: number,
  h: number,
  orientation: Orientation
): [Point, Point, Point, Point, Point, Point] {
  if (orientation === 'horizontal') {
    // Flat top/bottom — pointy left/right
    const inset = w * 0.25;
    return [
      { x: cx - w / 2 + inset, y: cy - h / 2 },   // top-left
      { x: cx + w / 2 - inset, y: cy - h / 2 },   // top-right
      { x: cx + w / 2, y: cy },                     // right
      { x: cx + w / 2 - inset, y: cy + h / 2 },   // bottom-right
      { x: cx - w / 2 + inset, y: cy + h / 2 },   // bottom-left
      { x: cx - w / 2, y: cy },                     // left
    ];
  }
  // Flat left/right — pointy top/bottom
  const inset = h * 0.25;
  return [
    { x: cx, y: cy - h / 2 },                     // top
    { x: cx + w / 2, y: cy - h / 2 + inset },     // top-right
    { x: cx + w / 2, y: cy + h / 2 - inset },     // bottom-right
    { x: cx, y: cy + h / 2 },                     // bottom
    { x: cx - w / 2, y: cy + h / 2 - inset },     // bottom-left
    { x: cx - w / 2, y: cy - h / 2 + inset },     // top-left
  ];
}

function getHexSides(vertices: [Point, Point, Point, Point, Point, Point]): LineSeg[] {
  const sides: LineSeg[] = [];
  for (let i = 0; i < 6; i++) {
    const next = (i + 1) % 6;
    sides.push({
      x3: vertices[i].x, y3: vertices[i].y,
      x4: vertices[next].x, y4: vertices[next].y,
    });
  }
  return sides;
}

function createHexagonShape(orientation: Orientation, value: string): ShapeDefinition {

  function drawPath(settings: ShapeDrawSettings): void {
    const { ctx, node, center: { x, y }, border, zoomLevel } = settings;
    const width = border ? node.width + node.borderWidth : node.width + node.borderWidth * 2;
    const height = border ? node.height + node.borderWidth : node.height + node.borderWidth * 2;
    const computedWidth = node.border ? width : node.width;
    const computedHeight = node.border ? height : node.height;

    const vertices = getVertices(x, y, computedWidth * zoomLevel, computedHeight * zoomLevel, orientation);
    ctx.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < 6; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].y);
    }
    ctx.closePath();
  }

  function getEdgeConnectionPoint(edge: EdgeData, connected: 'from' | 'to'): Point {
    const line1 = { x1: edge.startPosX, y1: edge.startPosY, x2: edge.endPosX, y2: edge.endPosY };

    const isFrom = connected === 'from';
    const nodeWidth = isFrom
      ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
      : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
    const nodeHeight = isFrom
      ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
      : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;
    const cx = isFrom ? edge.startPosX : edge.endPosX;
    const cy = isFrom ? edge.startPosY : edge.endPosY;

    const vertices = getVertices(cx, cy, nodeWidth, nodeHeight, orientation);
    const sides = getHexSides(vertices);
    return findEdgeSideIntersection(line1, sides);
  }

  function getCollisionData(edge: EdgeData, side: 'from' | 'to'): CollisionShape {
    const isFrom = side === 'from';
    const x = isFrom
      ? edge.startPosX - edge.fromNodeWidth / 2 - edge.fromNodeBorderWidthValue
      : edge.endPosX - edge.toNodeWidth / 2 - edge.toNodeBorderWidthValue;
    const y = isFrom
      ? edge.startPosY - edge.fromNodeHeight / 2 - edge.fromNodeBorderWidthValue
      : edge.endPosY - edge.toNodeHeight / 2 - edge.toNodeBorderWidthValue;
    const width = isFrom
      ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
      : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
    const height = isFrom
      ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
      : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;

    return { type: 'rect', data: { x, y, width, height } };
  }

  return {
    value,
    drawPath,
    getEdgeConnectionPoint,
    getCollisionData,
    resize: { constrainDimensions: false },
  };
}

const hexagonHorShape = createHexagonShape('horizontal', 'hexagon_hor');
const hexagonVertShape = createHexagonShape('vertical', 'hexagon_vert');

registerShape(hexagonHorShape);
registerShape(hexagonVertShape);

export { hexagonHorShape, hexagonVertShape };
